import { Client, Conversation } from '@twilio/conversations';

import { ApiError } from 'components/ErrorBoundary';
import { sleep } from 'utils/async';
import { DataDogLogger } from 'utils/dataDog';
import { pollUntilConditionMet } from 'utils/poll';

import { DEFAULT_RETRIES, DELAY_BETWEEN_RETRIES } from 'utils/axios/constants';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { MILLISECONDS_PER_SECOND } from 'constants/date';
import { TWILIO_CONNECTION_STATE } from 'constants/twilio';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { PropelAuthType } from 'services/propel/types';
import { TwilioType } from './types';

interface TwilioWsReturn extends TwilioType {
  elapsedTwilioInit: number;
  retryCount: number;
}

/**
 * @description - Initialize Twilio websocket Client and Twilio Conversation.
 * Websocket protocol is TCP stream, not HTTP. Our Axios retry logic will not work here.
 * Use custom looping logic to retry initializing connection.
 *
 * Initialization steps:
 * 1) Client - authenticate and initialize Twilio websocket client. Ref: https://media.twiliocdn.com/sdk/js/conversations/releases/2.1.0/docs/classes/Client.html
 * 2) Conversation - fetch synchronized conversationID. Ref: https://media.twiliocdn.com/sdk/js/conversations/releases/2.1.0/docs/classes/Conversation.html
 *
 * Recovery mechanisms:
 *  1) Twilio client retry initial call then repeat up to DEFAULT_RETRIES more times
 *  2) Get new tokens via startConversationAsync() then again repeat up to DEFAULT_RETRIES more times
 *  3) If twilio.client.connectionState is still connecting after initializing, poll for status until it is connected
 *
 * @param {PropelAuthType} propelAuth - Twilio conversation_id & access_token for authentication
 * @return {Promise<TwilioWsReturn> | Promise<Error>} - Twilio client & conversation with authenticated access to internal methods. Or error if failure. Includes performance metadata.
 */
export const getTwilio = async (propelAuth: PropelAuthType): Promise<TwilioWsReturn> => {
  let client: Client | undefined;
  let conversation: Conversation | undefined;
  let loggedError;
  const stampTwilioInitStart = Date.now();

  let twilioAccessToken = propelAuth.twilioAccessToken;
  let connectionStateBeforeNewTokens = undefined;
  let hasTriedFetchNewTokens = false;
  let retryCount = 0;

  // Recovery case 1: looping logic to retry with current tokens. Chance they are not instantaneously valid.
  do {
    if (retryCount > 0) await sleep(DELAY_BETWEEN_RETRIES / 2); // reduced since duplicated attempts with nested refresh for new tokens
    try {
      client = new Client(twilioAccessToken);
      // Recovery case 2: nested call to startConversation API to get new tokens in case current ones are invalid.
      if (retryCount === DEFAULT_RETRIES && !hasTriedFetchNewTokens) {
        hasTriedFetchNewTokens = true;
        connectionStateBeforeNewTokens = client.connectionState;
        try {
          /**
           * If API call successful, try to re-initialize Twilio client + conversation again, up to additional DEFAULT_RETRIES more times.
           * If unsuccessful after nested axiosWithRetry for startConversation API, log nested error at Twilio init level.
           */
          const startConvoNewTokens = await window[CHATUI_WINDOW_PATH].startConversationAsync(true);
          twilioAccessToken = startConvoNewTokens.twilioAccessToken;
          retryCount = 0;
        } catch (e: any) {
          const errorMsg = 'nested /conversation/start API retry failed.';
          throw new ApiError(errorMsg, { ...e });
        }
      }

      // lines above will trigger catch block if unsuccessful. Below is if successful.
      conversation = await client.getConversationByUniqueName(
        `conversation:${propelAuth.conversationId}`,
      );
      const isTwilioReady =
        conversation && client && client.connectionState === TWILIO_CONNECTION_STATE.CONNECTED;
      if (isTwilioReady && retryCount > 0) {
        retryCount = hasTriedFetchNewTokens ? retryCount + DEFAULT_RETRIES : retryCount;
        const successLogContext = {
          ...(connectionStateBeforeNewTokens && { connectionStateBeforeNewTokens }),
          ...propelAuth,
          hasTriedFetchNewTokens,
          retryCount,
        };
        DataDogLogger.action(CUSTOM_ACTION_NAMES.TWILIO_INIT_RETRY_SUCCESSFUL, successLogContext);
      }
      break;
    } catch (e) {
      loggedError = e;
      retryCount++;
    }
  } while (retryCount <= DEFAULT_RETRIES);

  // NOTE: Background error "failed to finish loading before timeout" will also trigger if this initialization process hangs.
  const errorLogContext = {
    ...(connectionStateBeforeNewTokens && { connectionStateBeforeNewTokens }),
    connectionState: client?.connectionState,
    error: loggedError,
    hasTriedFetchNewTokens,
    retryCount,
  };

  const pollingClientDescription = 'polling Twilio client status for connecting -> connected';
  const triggerTwilioClientError = () => {
    const errMsg = 'Twilio websocket client initialization failed';
    const clientError = new ApiError(errMsg, {
      ...errorLogContext,
      error: pollingClientDescription,
    });
    return Promise.reject(clientError);
  };
  if (client?.connectionState !== TWILIO_CONNECTION_STATE.CONNECTED) {
    try {
      // Recovery case 3: tokens are valid, APIs successful, but websocket may still be connecting
      const stampTwilioConnecting = Date.now();
      await pollUntilConditionMet({
        conditionFn: () => Boolean(client?.connectionState === TWILIO_CONNECTION_STATE.CONNECTED),
        description: pollingClientDescription,
        max: 60 * MILLISECONDS_PER_SECOND,
      });
      DataDogLogger.action(CUSTOM_ACTION_NAMES.TWILIO_INIT_POLLING_SUCCESSFUL, {
        elapsedPolling: Date.now() - stampTwilioConnecting,
      });
    } catch (e) {
      return triggerTwilioClientError();
    }
  }
  if (!client) {
    return triggerTwilioClientError();
  }

  if (!conversation) {
    const errMsg = 'Twilio conversation initialization failed.';
    const conversationError = new ApiError(errMsg, errorLogContext);
    return Promise.reject(conversationError);
  }

  const elapsedTwilioInit = Date.now() - stampTwilioInitStart;
  return { client, conversation, elapsedTwilioInit, retryCount };
};
